import get from 'lodash/get';

import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentCreditDebitViewController from 'app/src/controllers/payments/PaymentCreditDebitViewController';
import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import CCCharge from 'app/src/data/models/CCCharge';
import Localize from 'app/src/localize/Localize';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import PaymentCreditRefundByRefView from 'app/src/views/payments/PaymentCreditRefundByRefView';

const _Event = {
  REFUND_BY_REF_FINISHED: 'refund:by:ref:finished',
  REFUND_BY_INTERAC: 'refund:by:interac',
};

export default class PaymentCreditRefundByRefViewController extends BasePaymentController {
  static get Event() {
    return _Event;
  }

  /**
   * Constructor for a PaymentCreditRefundByRefViewController.
   *
   * @param {SaleManager} saleManager The associated sale manager
   * @param {Payment} payment The original payment that is being refunded
   * @param {PaymentProvider} paymentProvider The payment provider
   */
  constructor(saleManager, payment, paymentProvider) {
    super(saleManager);

    if (!payment) {
      throw 'A payment is required';
    }

    this._payment = payment;

    if (this._payment.isServicePayment()) {
      this._refund = payment.clone();
      this._refund.serviceModel.set({
        saleID: this._saleManager.sale.id,
        totalAmount: this._getMaximumRefund().negated(),
      });
    } else {
      // Create a refund payment.  Reference payments are treated as generic pending
      // payments.  Like with most things associated with payments this is subject to
      // change once more of the payment service is in place.
      this._refund = PaymentFactory.createPayment(
        PaymentTypeManager.Types.PENDING,
        this._getMaximumRefund(),
        this._payment.model.get('paymentTypeID')
      );

      // set the payment information; necessary for the
      // Sale Manager to recognize the payment as a credit/debit card
      // refund
      if (this._payment.model.has('PaymentType')) {
        this._refund.model.set('PaymentType', this._payment.model.get('PaymentType'));
      }
      if (this._payment.model.has('CCCharge')) {
        this._refund.model.set('CCCharge', this._payment.model.get('CCCharge'));
      }

      // Set the reference ID; this informs the API that this is a refund by reference
      // payment
      this._refund.model.set('refPaymentID', this._payment.model.id);
    }

    this._paymentProvider = paymentProvider;

    // initialize the controller's managed view
    const paymentData = this._payment.toJSON();

    // Handle card-on-file payments by setting up details in the JSON data
    if (this._payment.isCardOnFilePayment()) {
      // Extract last 4 digits from payment ID format: "cof_XXXX_YYYY"
      let maskedDigits = '';
      if (this._payment.model.get('paymentID')) {
        const parts = this._payment.model.get('paymentID').split('_');
        if (parts.length >= 2) {
          maskedDigits = parts[1];
        }
      }

      paymentData.details = {
        cardBrand: PaymentTypeManager.Types.CARD_ON_FILE,
        cardType: PaymentTypeManager.Types.CARD_ON_FILE,
        maskedDigits,
        transactionDate: this._payment.model.get('createTime')
          ? Format.parseDate(this._payment.model.get('createTime'))
          : '',
        isDebit: false,
        isInterac: false,
        isBancontact: false,
        isIdeal: false,
      };
    }

    this.view = new PaymentCreditRefundByRefView({
      viewData: {
        payment: paymentData,
        outstandingBalance: this._saleManager.pendingBalance,
      },
    });
  }

  /**
   * Sets the initial state of dynamic data within the view
   */
  setInitialViewState() {
    this.view.setMaximumRefund(this._getMaximumRefund());
    this.view.showOriginalChargeMessage(!this._chargeSettled());
  }

  /**
   * Event handler for when the amount to be refunded has changed.  The amount will
   * automatically revert to a valid amount if an invalid amount is input.  The amount
   * is limited to the outstanding balance or the original charge amount.
   *
   * @param amount {Decimal|string}   The new amount
   */
  onRefundByRefAmountChanged(amount) {
    let validatedAmount = Format.parseDecimal(amount);

    // make sure the value is valid
    if (validatedAmount instanceof Decimal) {
      validatedAmount = validatedAmount.abs();
      validatedAmount = Decimal.min(
        this._saleManager.pendingBalance.abs(),
        this._payment.amount.abs(),
        validatedAmount
      );
    } else {
      validatedAmount = Decimal.min(
        this._saleManager.pendingBalance.abs(),
        this._payment.amount.abs()
      );
    }

    if (this._payment.isServicePayment()) {
      this._refund.serviceModel.set({
        totalAmount: validatedAmount,
      });
    } else {
      // refunds are stored as negative payments...
      this._refund.amount = validatedAmount.negated();
    }

    this.view.setRefundAmount(validatedAmount);
  }

  /**
   * This method only gets triggered when the refund tip checkbox gets checked.
   * @param {boolean} refundTip default false.
   *
   * @returns {void}
   */
  onRefundByRefTipAmountClicked({ refundTip }) {
    this._refund.refundTip = refundTip;
    let totalAmount = this._getMaximumRefund();
    let tipAmount = new Decimal(0);

    if (refundTip) {
      tipAmount = this._payment.model.get('tipAmount').negated();
      totalAmount = totalAmount.plus(tipAmount);
    }

    if (this._payment.isServicePayment()) {
      this._refund.serviceModel.set({
        totalAmount: totalAmount.abs(),
      });
    } else {
      this._refund.amount = totalAmount.abs().negated();
      this._refund.tipAmount = tipAmount.abs().negated();
    }
    this.view.setRefundAmount(totalAmount);
  }
  /**
   * Event handler for when the Add Refund button is clicked.  This method constructs a pending
   * payment that is added to the Sale Manager.
   */
  onRefundByRefAddRefundClicked() {
    this.view.enableAddRefundButton(false);

    if (!this._payment.isServicePayment()) {
      this._saleManager
        .addPayment(this._refund)
        .done(() => {
          flashNotifier(
            'success',
            Localize.translate(
              `Credit Card <var>${this._payment.details.maskedDigits}</var> ` +
                `Payment Of <var>${this._refund.amount.abs()}</var> Added. Charge is NOT yet processed. ` +
                'It will be processed when you press Finish Sale.'
            ),
            9
          );
          this.trigger(_Event.REFUND_BY_REF_FINISHED);
        })
        .fail(() => {
          alertUser(Localize.translate('Failed to refund the payment.'));
          this.view.enableAddRefundButton(true);
        });
    } else if (this._payment.isCardOnFilePayment()) {
      // Get the current amount from the input field
      const currentAmount = this.view.getRefundAmount();
      let refundPayment = this._refund.clone();
      refundPayment.model.set('amount', currentAmount.negated());

      PaymentOperations.doRefundCardOnFilePaymentOperation(
        refundPayment,
        this._saleManager,
        this._paymentProvider
      )
        .done(() => {
          this.trigger(_Event.REFUND_BY_REF_FINISHED);
        })
        .fail(() => {
          alertUser(Localize.translate('Failed to refund the payment.'));
          this.view.enableAddRefundButton(true);
        });
    } else {
      const paymentTransaction = get(this._payment, 'serviceModel.paymentTransaction');
      if (paymentTransaction?.isInterac()) {
        // Interac refund is done here. The following messager trigger the event method `onRefundByInterac` in the `PaymentViewController` and pass down the value of `this.refund`.
        this.trigger(_Event.REFUND_BY_INTERAC, { refundedPayment: this._refund });
      } else {
        // This is a payment service-backed payment so do the refund immediately
        PaymentOperations.doVoidOperation(this._refund, this._saleManager, this._paymentProvider)
          .done(() => {
            this.trigger(_Event.REFUND_BY_REF_FINISHED);
          })
          .fail(() => {
            alertUser(Localize.translate('Failed to refund the payment.'));
            this.view.enableAddRefundButton(true);
          });
      }
    }
  }

  /**
   * Event handler for when the Cancel button is clicked
   */
  onRefundByRefCancelClicked() {
    this.trigger(_Event.REFUND_BY_REF_FINISHED);
  }

  /**
   * Determines the maximum amount a user can refund
   *
   * @private
   */
  _getMaximumRefund() {
    return Decimal.min(
      this._saleManager.pendingBalance.abs(),
      this._payment?.maximumRefundAmount
    ).negated();
  }

  /**
   * Determines if the charge has been settled.  Refunds to non-settled amounts can only be refunded at
   * the full amount.
   *
   * @returns {boolean}   True if the charge has been settled.
   * @private
   */
  _chargeSettled() {
    let hasSettled = false;
    let ccCharge = this._payment.model.getRelation('CCCharge', CCCharge);

    // there must be a better way to do this other than end of day but that's how the legacy
    // code works
    if (ccCharge) {
      let transactionDate = Format.parseDate(ccCharge.get('timeStamp'));
      let currentDate = Format.parseDate(new Date().toString());

      hasSettled = transactionDate !== currentDate;
    }

    return hasSettled;
  }
}
